import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { server } from '../server'; 
import UserProfileData from '../components/UserProfileData';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import UserBanner from '../components/Shop/UserBanner';
import MyImages from '../components/Content/MyImages';
import MyContentCarousel from '../components/Profile/MyContentCarousel';
import RoomsProfile from '../components/Rooms/RoomsProfile';
import FollowersDisplay from '../components/Connections/FollowersDisplay';
import MyPostWidgetRedux from '../widgets/MyPostWidgetRedux';
import MixedSponsoredAds from '../components/Sponsored/MixedSponsoredAds';
import FollowingDisplay from '../components/Connections/FollowingDisplay';
import UserAvatar from '../components/Layout/UserAvatar';
import ImageCropperModal from '../components/Image/ImageCropperModal';
import { useDispatch } from 'react-redux';
import { loadUser } from '../redux/actions/user';
import { toast } from 'react-toastify';
import Loader from '../components/Layout/Loader';
import EventsParentContainer from '../components/Events/EventsParentContainer';
import {
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from '../utils/imageUploadPreparation';
import { buildPublicAppUrl } from '../utils/publicApp';
import '../stylesheets/UserProfileHeroPills.css';
import '../stylesheets/ProfileFeatureWidgetRow.css';
import ShareToSocialMedia from '../components/Shop/ShareToSocialMedia';

const PrivateProfilePage = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAvatarCropper, setShowAvatarCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // 1) Renamed `id` to `handle`:
  const { handle } = useParams();
  const dispatch = useDispatch();
  const currentProfile = sessionStorage.getItem("currentProfile");

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // 2) Fetch user by handle instead of by _id:
        const response = await axios.get(`${server}/user/user-info/handle/${handle}`);
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [handle]);

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (!file) {
      return;
    }

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setSelectedFile(preparedFile);
      setShowAvatarCropper(true);
    } catch (error) {
      console.error('Error preparing user avatar:', error);
      toast.error(getImagePreparationErrorMessage(error));
    }
  };

  const updateAvatar = async (avatarFile) => {
    try {
      const formData = new FormData();
      formData.append("avatar", avatarFile);

      await axios.put(`${server}/user/update-avatar`, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
          "Current-Profile": currentProfile,
        },
      });
      dispatch(loadUser());
      const previewUrl = URL.createObjectURL(avatarFile);
      setUser((prevUser) => ({ ...prevUser, avatar: { url: previewUrl } }));
      toast.success("Avatar updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update avatar");
      throw error;
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (!user) {
    return <div>User data not available</div>;
  }

  // 3) We'll pass the user's _id to child components that require the MongoDB _id:
  const userId = user._id;
  const profileShareUrl = typeof window !== 'undefined' ? window.location.href : buildPublicAppUrl(`/user/${user.handle}`);

  return (
    <div className="profile-page profile-owner-view">
      <Header />
      <Sidebar />
      <div className="profile-banner-wrapper">
        <UserBanner isUser={true} user={user} setUser={setUser} onAvatarChange={handleAvatarChange} />
        <div className="profile-info-container">
          <div className="profile-hero-layout">
            <UserAvatar user={user} />
            <div className="profile-info">
              <div className="profile-name-wrapper">
                <div className="profile-name-and-button">
                  <h3 className="profile-name">{user.name}</h3>
                  <div className="profile-hero-spacer" aria-hidden="true" />
                </div>
                <h3 className="profile-handle">@{user.handle}</h3>
                <div className="profile-friends user-profile-hero-connection-pills">
                  <FollowersDisplay userId={userId} profileType="User" />
                  <FollowingDisplay userId={userId} profileType="User" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-main-content">
        <div className="profile-sidebar-left">
          <RoomsProfile
            profileId={userId}
            profileType="User"
            canCreate={true}
            showActionMenu
            emptyMessage="Create your first community to get started."
          />
          <MyImages
            userId={userId}
            canAddPhotos={true}
            emptyActionHref="#profile-post-composer"
            emptyMessage="Share a photo post to start your gallery."
            ownerName={user.name}
            ownerAvatar={user.avatar?.url}
          />
          <div className="profile-share-sidebar-card">
            <h5>Share Profile</h5>
            <ShareToSocialMedia
              url={profileShareUrl}
              message={`Check out ${user.name} on MATRIX.`}
              profileName={user.name}
              profileHandle={user.handle}
              qrCodeUrl={user.qrCodeUrl || ''}
              title="Share Profile"
            />
          </div>
          <EventsParentContainer ownerId={userId} />
        </div>
        
        <main className="profile-main-area">
          <div id="profile-post-composer" className="profile-post-composer-anchor">
            <MyPostWidgetRedux />
          </div>
          <div className="profile-feature-widget-row profile-mobile-feature-widget-row" aria-label="Profile media widgets">
            <section className="profile-feature-widget profile-feature-widget-photos">
              <MyImages
                userId={userId}
                canAddPhotos={true}
                emptyActionHref="#profile-post-composer"
                showActionMenu
                emptyMessage="Share a photo post to start your gallery."
                ownerName={user.name}
                ownerAvatar={user.avatar?.url}
              />
            </section>
            <section className="profile-feature-widget profile-feature-widget-memes">
              <MyContentCarousel ownerId={userId} profileType="User" canCreate={true} showActionMenu />
            </section>
            <section id="profile-communities-widget" className="profile-feature-widget profile-feature-widget-communities">
              <RoomsProfile
                profileId={userId}
                profileType="User"
                canCreate={true}
                showActionMenu
                emptyMessage="Create your first community."
              />
            </section>
          </div>
          <UserProfileData initialUser={user} />
          <div className="profile-lower-mobile-stack" aria-label="Profile recommendations and sponsored content">
            <EventsParentContainer ownerId={userId} />
            <MixedSponsoredAds />
          </div>
        </main>

        <aside className="profile-sidebar-right">
          <MyContentCarousel ownerId={userId} profileType="User" canCreate={true} />
          <MixedSponsoredAds />
        </aside>
      </div>

      {/* Avatar Image Cropper Modal */}
      {showAvatarCropper && (
        <ImageCropperModal
          updateAvatar={updateAvatar}
          closeModal={() => setShowAvatarCropper(false)}
          initialImage={selectedFile}
          aspectRatio={1}
          minWidth={200}
          isAvatar={true}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
};

export default PrivateProfilePage;


